import React, { useEffect, useMemo, useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import { conformToMask } from 'react-text-mask';

import { states } from 'utils/states';
import ContentWrapper from 'components/ContentWrapper';
import User from 'assets/images/pending.svg';

import {
  newNextAction,
  NEW_NEXT,
} from '../../actions/navigation';

import Container from '../../components/Container';
import FormRow from '../../components/Form/FormRow';
import FormColumn from '../../components/Form/FormColumn';
import Input from '../../components/Form/Input';
import Validator from '../../components/Validator';
import Button from '../../components/Button';
import Select from '../../components/Form/Select';
import { useConfig } from '../../config/ConfigContext';
import Header from '../../components/Header';
import ApplicationId from '../../components/ApplicationId';
import schema from './schema';
import './style.scss';

const phoneNumberMask = ['(', /\d/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/];

function Confirmation(props) {
  const config = useConfig();
  const { validator: { values, errors }, checkinData, location, searchParams, params } = props;
  const key = searchParams.get('key');
  const [isLoading, setIsLoading] = useState(false);
  const [isValid, setIsValid] = useState(true);
  const isSsn4Mismatch = useMemo(() => get(checkinData, 'data.isSsn4Mismatch'), [checkinData]);
  const errorMessage = useMemo(() => get(checkinData, 'data.errorMessage'), [checkinData]);

  useEffect(() => {
    const { navigate, validator: { setValues } } = props;
    const workflowType = get(params, 'workflowType');
    const test = searchParams.get('test');

    if (get(checkinData, ['data']) === undefined) {
      if (test) {
        navigate(`${workflowType ? `/${workflowType}` : ''}/?key=${key}&test=${test}`);
      } else {
        navigate(`${workflowType ? `/${workflowType}` : ''}/?key=${key}`);
      }
    } else {
      const initialFormData = {
        applicantFirstName: get(checkinData, 'data.applicantFirstName'),
        applicantFullName: get(checkinData, 'data.applicantFullName'),
        streetAddress1: get(checkinData, 'data.streetAddress1'),
        city: get(checkinData, 'data.city'),
        state: get(checkinData, 'data.state'),
        zipCode: get(checkinData, 'data.zipCode'),
        phoneNumber: conformToMask(get(checkinData, 'data.phoneNumber'), phoneNumberMask).conformedValue,
      };
      setValues(initialFormData);
    }
  }, []);

  useEffect(() => {
    const { fetch, navigate, validator: { setValues } } = props;
    const initialFormData = {
      applicantFirstName: get(checkinData, 'data.applicantFirstName'),
      applicantFullName: get(checkinData, 'data.applicantFullName'),
      streetAddress1: get(checkinData, 'data.streetAddress1'),
      city: get(checkinData, 'data.city'),
      state: get(checkinData, 'data.state'),
      zipCode: get(checkinData, 'data.zipCode'),
      phoneNumber: conformToMask(get(checkinData, 'data.phoneNumber'), phoneNumberMask).conformedValue,
    };
    setValues(initialFormData);

    if (fetch.isDone[`${NEW_NEXT}`] && !fetch.isRunning[`${NEW_NEXT}`]) {
      const routeUrl = checkinData.data && checkinData.data.url.replace(config.hostUrl, '');
      navigate(routeUrl);
    }
  }, [checkinData]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  const handleInputChange = (event) => {
    const { validator: { onChangeHandler } } = props;
    onChangeHandler(event.target.name, event.target.value);
  };

  const handleSubmitForm = (e) => {
    e.preventDefault();

    if (key) {
      const { validator: { validate }, dispatch } = props;
      if (validate(schema).isValid) {
        const workflowType = get(params, 'workflowType') || 'text2apply';
        setIsLoading(true);
        setIsValid(true);
        dispatch(newNextAction({
          data: {
            StreetAddress1: values.streetAddress1,
            City: values.city,
            State: values.state,
            ZipCode: values.zipCode,
            Ssn4: values.Ssn4,
            PhoneNumber: values.phoneNumber,
          },
          key,
          workflowType,
          success: (response) => {
            const routeUrl = response.data && response.data.url;
            setIsLoading(false);
            props.navigate(routeUrl);
          },
          fail: (error) => {
            setIsLoading(false);
            props.navigate({
              pathname: '/results/denied',
              search: '',
              state: {
                data: error.data,
              },
            });
          },
        }));
      } else {
        validate(schema);
        setIsValid(false);
      }
    }
  };

  return (
    <div className="transition-item wrapper v-center">
      <form onSubmit={handleSubmitForm.bind(null, values)}>
        <Header />
        <ApplicationId id={get(checkinData, 'attributes.entityId')} style={{ marginTop: 10 }} />
        <ContentWrapper className="content appIdVisible" style={{ padding: '10px 20px' }}>
          <Container className="container">
            <FormRow>
              <FormColumn>
                <h2 className="text-left heading-h">Identity Verification</h2>
                <div className="d-flex align-items-center">
                  <img src={User} alt="User Icon" className="user-image" />
                  <div className="pl-10">
                    <div>LendingUSA</div>
                  </div>
                </div>
              </FormColumn>
            </FormRow>
            <FormRow>
              <FormColumn>
                {!isLoading
                && isSsn4Mismatch
                && (
                  <div className="pt-10">
                    <p className="form-note text-left">{errorMessage}</p>
                  </div>
                )}
                <h4 className="pt-20 text-left">ID Check - Personal Information</h4>
                <p className="heading-paragraph pt-10 text-left">Please confirm your home address and phone number. This information, along with your name, will be used to generate a list of questions to verify your identity.</p>
                <h4 className="pt-20 text-left">Required Information (Home Address)</h4>
              </FormColumn>
            </FormRow>
            <FormRow className="pt-10">
              <FormColumn>
                <Input
                  type="text"
                  name="name"
                  onChange={() => {}}
                  label="Applicant Name"
                  value={values.applicantFullName}
                  isBadgeVisible={false}
                  isDisabled
                />
              </FormColumn>
            </FormRow>
            <FormRow>
              <FormColumn>
                <Input
                  type="text"
                  name="streetAddress1"
                  label="Street1"
                  value={values.streetAddress1}
                  onChange={handleInputChange}
                  isRequired
                  hasError={!!errors.streetAddress1}
                  errorMessage={errors.streetAddress1}
                />
              </FormColumn>
            </FormRow>
            <FormRow>
              <FormColumn>
                <Input
                  type="text"
                  name="city"
                  value={values.city}
                  label="City"
                  onChange={handleInputChange}
                  isRequired
                  hasError={!!errors.city}
                  errorMessage={errors.city}
                />
              </FormColumn>
            </FormRow>
            <FormRow>
              <FormColumn>
                <Select
                  name="state"
                  value={values.state}
                  onChange={handleInputChange}
                  data={states}
                  label="State"
                  isRequired
                  hasError={!!errors.state}
                  errorMessage={errors.state}
                />
              </FormColumn>
            </FormRow>
            <FormRow sm={12} className="d-flex align-items-center">
              <FormColumn>
                <Input
                  label="Zip"
                  isMasked={[/\d/, /\d/, /\d/, /\d/, /\d/]}
                  placeHolder="_____"
                  onChange={handleInputChange}
                  name="zipCode"
                  value={values.zipCode}
                  isRequired
                  hasError={!!errors.zipCode}
                  errorMessage={errors.zipCode}
                />
              </FormColumn>
            </FormRow>
            <FormRow>
              <FormColumn>
                <Input
                  type="tel"
                  name="Ssn4"
                  value={values.Ssn4}
                  onChange={handleInputChange}
                  label="Last 4 digits of SSN"
                  isMasked={[/\d/, /\d/, /\d/, /\d/]}
                  isRequired
                  hasError={!!errors.Ssn4}
                  errorMessage={errors.Ssn4}
                />
              </FormColumn>
            </FormRow>
            <FormRow>
              <FormColumn>
                <Input
                  type="text"
                  name="phoneNumber"
                  isMasked={['(', /[1-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]}
                  value={conformToMask(values.phoneNumber, phoneNumberMask).conformedValue}
                  onChange={handleInputChange}
                  label="Phone #"
                  isRequired
                  hasError={!!errors.phoneNumber}
                  errorMessage={errors.phoneNumber}
                />
              </FormColumn>
            </FormRow>
            <FormRow>
              <FormColumn>
                {!isValid && (
                  <div>
                    <p className="form-note">You must enter required and valid information before you can continue.</p>
                  </div>
                )}
              </FormColumn>
            </FormRow>
          </Container>
        </ContentWrapper>
        <div className="footer">
          <FormRow>
            <FormColumn>
              <Button
                color="primary"
                className="large buttonStyle btn-primary"
                onClick={handleSubmitForm}
                isLoading={isLoading}
                isDisabled={isLoading}
                title="Submit"
              />
            </FormColumn>
          </FormRow>
        </div>
      </form>
    </div>
  );
}

Confirmation.propTypes = {
  navigate: PropTypes.func.isRequired,
  checkinData: PropTypes.object.isRequired,
  validator: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  fetch: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  params: PropTypes.object.isRequired,
  searchParams: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  fetch: state.fetch,
  checkinData: state.screen.checkinData,
});

export default compose(
  Validator(schema),
  connect(mapStateToProps)
)(Confirmation);
